import React from 'react';

import { useIsInNestedPlaceholder } from 'contexts';
import type { HTMLAttributes, HTMLTagName } from 'types';
import { cn } from 'utils/classNames';

import GridOverlay from './GridOverlay';
import { getContainerMaxWidthClass, type MaxWidth } from './helpers';

export const GUTTER_CLASSES = 'px-4 md:px-6';
export const PULL_GUTTER_CLASSES = '-mx-4 md:-mx-6';
export const SM_GUTTER_CLASSES = 'sm:px-4 md:px-6';
export const SM_PULL_GUTTER_CLASSES = 'sm:-mx-4 md:-mx-6';
export const MD_GUTTER_CLASSES = 'md:px-6';
export const MD_PULL_GUTTER_CLASSES = 'md:-mx-6';

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'className'> {
	/** CSS color value, like hex. */
	backgroundColor?: string;

	/** React children */
	children?: React.ReactNode;

	/** Include standard horizontal edge spacing classes */
	gutters?: boolean | 'sm' | 'md';

	/** CSS color value, like hex. */
	innerBackgroundColor?: string;

	/** Extra classes for the inner container */
	innerClassName?: string;

	/** Component to use for the inner element */
	innerComponent?: HTMLTagName;

	/** If nested inside other containers (disables max width and gutters) */
	isNested?: boolean;

	/** max-width for the container. */
	maxWidth?: MaxWidth;

	/** Extra classes for the outer container */
	outerClassName?: string;

	/** Component to use for the outer element */
	outerComponent?: HTMLTagName;

	/** Show a grid overlay */
	overlay?: boolean;

	/** Set standard grid classes on the inner element. */
	withGrid?: boolean;

	/** Set vertical grid gap matching the horizontal one. */
	withVerticalGridGap?: boolean;
}

/** Layout container setting the page width. */
const LayoutContainer = React.forwardRef<HTMLDivElement, Props>(
	(
		{
			backgroundColor,
			children,
			gutters = true,
			innerBackgroundColor,
			innerClassName = '',
			innerComponent: InnerComponent = 'div',
			isNested: IsNestedProp = false,
			maxWidth = 'standard',
			outerClassName = '',
			outerComponent: OuterComponent = 'div',
			overlay = false,
			withGrid = false,
			withVerticalGridGap = false,
			...outerAttrs
		},
		ref,
	) => {
		const isInNestedPlaceholder = useIsInNestedPlaceholder();
		const isNested = IsNestedProp || isInNestedPlaceholder;

		return (
			<OuterComponent
				ref={ref}
				style={backgroundColor ? { backgroundColor } : undefined}
				className={cn(
					outerClassName,
					backgroundColor && 'py-8 md:py-12',
					gutters &&
						!isNested && [
							'print:!px-0',
							gutters === true && GUTTER_CLASSES,
							gutters === 'sm' && SM_GUTTER_CLASSES,
							gutters === 'md' && MD_GUTTER_CLASSES,
						],
					overlay && 'relative',
				)}
				{...outerAttrs}
			>
				<InnerComponent
					style={
						innerBackgroundColor
							? { backgroundColor: innerBackgroundColor }
							: undefined
					}
					className={cn(
						innerClassName,
						!isNested && getContainerMaxWidthClass(maxWidth),
						'mx-auto',
						withGrid && 'grid grid-cols-4 gap-x-4 md:grid-cols-12 md:gap-x-6',
						withVerticalGridGap && 'gap-y-4 md:gap-y-6',
					)}
				>
					{children}
				</InnerComponent>
				{overlay && <GridOverlay maxWidth={maxWidth} />}
			</OuterComponent>
		);
	},
);
LayoutContainer.displayName = 'LayoutContainer';

export default LayoutContainer;
